import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { useAuth } from "@/contexts/AuthContext";
import { COLLECTIONS } from "@/integrations/firebase/collections";
import { logError } from "@/shared/services/ErrorReporting";
import { db } from "@/integrations/firebase/firestore";
import { currentStage } from "@/components/requests/requestPhase";
import type { Request } from "./types";

/** What this viewer is being waited on for, on one request. */
export type DecisionPart =
  /** Theirs to settle now: whether it goes ahead, as the named approver. */
  | "APPROVE"
  /** Theirs to act on now: provide the access, as the named activator. */
  | "ACTIVATE"
  /** Their request, but not their turn — the approver has it. */
  | "WAITING";

export interface QueuedAccessRequest {
  request: Request;
  part: DecisionPart;
}

export interface UseAccessDecisionQueueResult {
  /** Waiting on this viewer right now, newest first. Nothing else: a request
   *  that has not reached them is not in here (see below). */
  mine: QueuedAccessRequest[];
  loading: boolean;
  error: Error | null;
}

/**
 * The access requests waiting on the person signed in — as the approver a
 * requester named, as the activator their unit configured, or both.
 *
 * **Only what has actually reached them.** The queue used to carry a second
 * group as well, the requests still with their approver, on the reasoning that
 * a desk which can see what is coming can plan for it. The August test run
 * settled it the other way (Jira NAV-70): the desk read a list mixing work it
 * could do with work it could not, and asked to be shown the first only. So
 * the activator's listener still finds a request from the moment it is raised
 * — the rules grant that, and the detail page still opens — but this queue
 * lists it when it is theirs. Nothing is lost on the approver's side, who is
 * first in the line and has nothing upstream to preview.
 *
 * **Keyed on the real signed-in address, not the acting profile's.** Everywhere
 * else a read follows `actingUid`, so a super admin viewing as someone else
 * sees that person's data. A decision is not a read: `firestore.rules` matches
 * `request.auth.token.email`, which is always the admin's own, so a queue built
 * from the impersonated address would list work its viewer could not do. The
 * same reasoning `AuthContext` gives for keeping writes attributed to the real
 * user.
 *
 * Two listeners rather than one `or()` query, and no `orderBy`: the rules
 * authorize per document and a query has to be provably allowed as a whole, so
 * two narrow equality reads are the safe shape — and one person's queue is a
 * handful of documents, which is the same trade `useResponsibleRequests` makes
 * and for the same reason (an ordered field beside these filters would need a
 * composite index for no gain).
 *
 * **The cost, stated because the nav bar pays it for everybody.** Most members
 * are neither an approver nor an activator, and for them both listeners match
 * nothing — two empty equality reads per session, alongside the one
 * `useResponsibleStepCount` already opens for the same reason. Cheap, but not
 * free, and the only way to avoid it would be to know the answer before
 * asking. If it ever matters, the lever is the unit's own configuration: a unit
 * with no `requestRouting` can route nothing, so its members could skip both
 * subscriptions on one BU read.
 */
export function useAccessDecisionQueue(): UseAccessDecisionQueueResult {
  const { user } = useAuth();
  const email = user?.email?.toLowerCase() ?? null;
  const [asApprover, setAsApprover] = useState<Request[] | null>(null);
  const [asActivator, setAsActivator] = useState<Request[] | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Reset first, so an address that goes away clears the failure with it. The
    // early return below hands back two empty lists and `loading: false` — with
    // a stale error beside them that reads as a refused queue, which the
    // navigation then offers for ever as a badge-less entry (NAV-134).
    setError(null);
    if (!email) {
      setAsApprover([]);
      setAsActivator([]);
      return;
    }
    setAsApprover(null);
    setAsActivator(null);

    const subscribe = (
      field: "approverEmail" | "activatorEmail",
      onRows: (rows: Request[]) => void,
    ) =>
      onSnapshot(
        query(
          collection(db, COLLECTIONS.REQUESTS),
          where(field, "==", email),
          where("status", "==", "PENDING"),
        ),
        (snap) =>
          onRows(
            snap.docs.map((d) => ({
              ...(d.data() as Omit<Request, "id">),
              id: d.id,
            })),
          ),
        (err) => {
          logError(err);
          setError(err);
          onRows([]);
        },
      );

    const unsubApprover = subscribe("approverEmail", setAsApprover);
    const unsubActivator = subscribe("activatorEmail", setAsActivator);
    return () => {
      unsubApprover();
      unsubActivator();
    };
  }, [email]);

  return useMemo(() => {
    const loading = asApprover === null || asActivator === null;
    if (loading || !email) {
      return { mine: [], loading, error };
    }
    // One person can be both this unit's approver and its activator, so the
    // same request can arrive down both listeners. Which part is *theirs* is
    // decided by where the request has got to, not by which query found it.
    const byId = new Map<string, Request>();
    for (const r of [...asApprover, ...asActivator]) byId.set(r.id, r);

    const queued = [...byId.values()]
      .map((request) => ({
        request,
        part: partFor(request, email, user?.uid),
      }))
      .sort(
        (a, b) =>
          (b.request.createdAt?.toMillis() ?? 0) -
          (a.request.createdAt?.toMillis() ?? 0),
      );

    return {
      // WAITING is "not this viewer's turn" — their own request, or one still
      // with the other party. Neither is waiting on them.
      mine: queued.filter((q) => q.part !== "WAITING"),
      loading: false,
      error,
    };
  }, [asApprover, asActivator, email, user?.uid, error]);
}

/**
 * Whose turn it is, from the viewer's side. Exported so the detail page — which
 * loads one request rather than a queue — reaches the same answer.
 */
export function partFor(
  request: Request,
  email: string,
  uid?: string | null,
): DecisionPart {
  const stage = currentStage(request);
  const mine =
    stage === "APPROVE"
      ? request.approverEmail === email
      : request.activatorEmail === email;
  // Nobody decides their own request, whatever the unit configured — the rules
  // refuse it outright, so offering the buttons would be offering a write that
  // fails. It happens for real: the person who runs a unit's service desk asks
  // for tools like anybody else, and their own address is the activator on the
  // request they just raised. Their admin unsticks it.
  if (uid && request.userId === uid) return "WAITING";
  // Anyone else looking is a bystander, which on this screen means waiting.
  return mine ? stage : "WAITING";
}
